import MuxPlayer from '@mux/mux-player-react';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import styled from 'styled-components';

import type MuxPlayerElement from '@mux/mux-player';
import type { MuxPlayerElementEventMap } from '@mux/mux-player';
import type { ClipSourceRange } from '@src/services/reel/clipRangeWords';
import type { Story } from '@src/types.ts/story';
import type { CSSProperties } from 'react';

/**
 * How far short of a passage's end the handover to the next one fires.
 *
 * `timeupdate` lands about four times a second, so a boundary tested exactly
 * would be crossed by up to a quarter of a second of the passage after it —
 * audible as a stutter of the wrong audio at every join. `MuxPlayerMultiClip`
 * pads by the same amount for the same reason.
 */
const TIME_UPDATE_PADDING_SECONDS = 0.265;

/** The chrome off: this plays in a 64×36 frame, which has room for none of it. */
const playerStyle: CSSProperties & { [key: `--${string}`]: string } = {
  width: '100%',
  height: '100%',
  '--controls': 'none',
  '--media-object-fit': 'cover',
};

export interface ClipRowPreviewHandle {
  /** Move playback to a time on the clip's own clock. */
  seek: (clipTime: number) => void;
}

interface ClipRowPreviewPlayerProps {
  /**
   * The passages the candidate contributes, in playing order — one for an
   * ordinary clip, one per `subClips` entry for a reel (DE15).
   */
  ranges: readonly ClipSourceRange[];
  /** The story the passages are cut from; every candidate is a range of it (DE16). */
  story?: Story | null;
  /** The row's own frame, so the preview does not open on black. */
  poster?: string;
  /** Play, or hold where it is. */
  isPlaying: boolean;
  /** Where playback has reached, on the clip's own clock. */
  onTimeUpdate: (clipTime: number) => void;
  /** The last passage ran out — the row goes back to being a still (DS7). */
  onEnded: () => void;
}

/**
 * PLAT-11109 — the player of a single Add Clips picker row, inside the row's own
 * frame.
 *
 * Mounted by the playing row and by no other (DS3): the picker lists every
 * combinable clip in the story, and a player per row would be a media element
 * per row on a list of dozens. Starting a second row therefore stops the first
 * by construction, since the first unmounts.
 *
 * It is deliberately **not** the clip preview's own player behind the modal
 * (DS2). That one holds the reel being edited — loading a candidate into it
 * would tear down the edit in progress, and it is the renderer iframe, which a
 * modal must not take focus from (CLAUDE.md pitfall 10).
 *
 * One media element, seeking passage to passage, rather than
 * `MuxPlayerMultiClip`'s element-per-cut: every passage of every candidate is a
 * range of the *same* source video, so there is nothing for a second element to
 * hold. What is taken from that player is the shape of the handover — play to
 * the end of a passage, then move to the start of the next.
 */
export const ClipRowPreviewPlayer = React.forwardRef<
  ClipRowPreviewHandle,
  ClipRowPreviewPlayerProps
>(function ClipRowPreviewPlayer(
  { ranges, story, poster, isPlaying, onTimeUpdate, onEnded },
  ref,
) {
  const playerRef = useRef<MuxPlayerElement | null>(null);
  /** Which passage is playing. A ref: `timeupdate` reads it several times a second. */
  const passageRef = useRef(0);

  /** Where each passage starts on the clip's own clock. */
  const offsets = useMemo(() => {
    let total = 0;
    return ranges.map(range => {
      const start = total;
      total += range.duration;
      return start;
    });
  }, [ranges]);

  // The source, resolved the way the Transcript & Speakers player resolves it:
  // the direct MP4 where the story has one (it loads and seeks faster), Mux
  // streaming otherwise.
  const originalVideo = story?.originalVideo;
  const directMp4Url =
    originalVideo?.video?.mp4UrlHigh ||
    originalVideo?.video?.mp4UrlHd ||
    originalVideo?.video?.mp4Url ||
    originalVideo?.video?.mp4UrlMedium ||
    originalVideo?.video?.mp4UrlLow;
  const playbackId = directMp4Url
    ? undefined
    : originalVideo?.video?.muxPlaybackId;

  /** Puts the element on a passage, at an offset into it. */
  const playAt = useCallback(
    (passage: number, secondsIn: number) => {
      const player = playerRef.current;
      const range = ranges[passage];
      if (!player || !range) return;
      passageRef.current = passage;
      player.currentTime = range.startTime + secondsIn;
    },
    [ranges],
  );

  useImperativeHandle(
    ref,
    () => ({
      seek: (clipTime: number) => {
        // The passage the time lands in — the last one that starts at or before
        // it, so a time past the end parks in the final passage rather than
        // falling off the list.
        let passage = 0;
        for (let index = 0; index < offsets.length; index++) {
          if (clipTime >= offsets[index]) passage = index;
        }
        const range = ranges[passage];
        if (!range) return;
        const secondsIn = Math.min(
          Math.max(0, clipTime - offsets[passage]),
          range.duration,
        );
        playAt(passage, secondsIn);
        // Reported straight back rather than waiting for the element: a seek
        // while paused would otherwise leave the bar where the drag started.
        onTimeUpdate(offsets[passage] + secondsIn);
      },
    }),
    [offsets, ranges, playAt, onTimeUpdate],
  );

  // Play / pause from the row's control. Not `autoPlay`, which only speaks at
  // load: the row can be paused and resumed without remounting.
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    // `play()` rejects when the element is not ready to start (a source still
    // resolving, an autoplay policy) — swallowed, because the row's control is
    // the only thing that says whether it is playing, and an unhandled
    // rejection here would surface as a console error on an ordinary click.
    if (isPlaying) player.play?.()?.catch(() => {});
    else player.pause?.();
  }, [isPlaying]);

  const handleTimeUpdate = (event: MuxPlayerElementEventMap['timeupdate']) => {
    const sourceTime = (event.target as HTMLVideoElement).currentTime;
    const passage = passageRef.current;
    const range = ranges[passage];
    if (!range) return;

    const rangeEnd = range.startTime + range.duration;
    if (sourceTime >= rangeEnd - TIME_UPDATE_PADDING_SECONDS) {
      if (passage < ranges.length - 1) {
        playAt(passage + 1, 0);
        onTimeUpdate(offsets[passage + 1]);
      } else {
        playerRef.current?.pause?.();
        onEnded();
      }
      return;
    }

    // Before the passage starts is the element still arriving at a seek — the
    // clip's clock has not moved, so nothing is reported for it.
    if (sourceTime < range.startTime) return;
    onTimeUpdate(offsets[passage] + (sourceTime - range.startTime));
  };

  return (
    <Frame
      // The row is a pick target; a click landing on the video is not a pick,
      // and the play control on top of this is what stops and starts it.
      onClick={e => e.stopPropagation()}
    >
      <MuxPlayer
        ref={playerRef}
        style={playerStyle}
        streamType="on-demand"
        playbackId={playbackId}
        src={directMp4Url}
        // Where the first passage begins. Only read at load, which is why every
        // later move goes through `playAt`.
        startTime={ranges[0]?.startTime ?? 0}
        poster={poster}
        autoPlay
        volume={1}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
      />
    </Frame>
  );
});

/** Fills the row's frame, under the play control that started it. */
const Frame = styled.div`
  position: absolute;
  inset: 0;
  background-color: #000;
`;
